import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid2, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import { DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { differenceInCalendarDays, format } from 'date-fns';
import useApi from '../hooks/useApi';
import { ACTIVITIES_MAX_DAYS, dataGridHeaderHeight, dataGridRowHeight, GLOBALSETTINGS, GRIDCOLUMNKEYS, ModuleNames, SEARCHBOX, SYSTEMSETTINGS } from '../data/constants';
import { AuditTraceRequest } from '../types/AuditLog';
import { AuditNavigationTrace } from '../types/AuditNavigationTrace';
import { useUser } from '../context/UserContext';
import { getNavigationTraces } from '../services/auditNavigationTraceService';
import SearchBox from '../components/form/SearchBox/SearchBox';
import { useSettingValue } from '../services/useSettingValue';
import { filterDataBySearch, handleScrollPositionChange } from '../utils/commonHelper';
import CustomLoadingOverlay from '../components/ui/CustomLoadingOverlay';
import { useMenu } from '../context/MenuContext';
import UsersDropdownList from '../components/ui/UsersDropdownList';
import { GLOBALMESSAGES } from '../constants/messages';
import { getCurrentESTDate } from '../utils/dateHelper';
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePersistedColumnVisibility } from "../hooks/usePersistedColumnVisibility";

/**
 * Epic 80054 - true when an Activities query must NOT be issued because it would scan an
 * unbounded slice of the navigation-trace log: a range wider than ACTIVITIES_MAX_DAYS with no
 * user selected to scope it. Shared by the Go handler (which shows the guard message) and
 * loadData (which also runs on search and pagination changes), so the two cannot disagree.
 *
 * Deliberately the same shape as the Audit Log's isSqlaRangeBlocked, down to the escape hatch:
 * an empty user list means the sproc filters on nobody and returns EVERY user's rows, which is
 * the expensive case; scoping to users lifts the day limit. It is also a calendar-day
 * DIFFERENCE, so 3 permits a span of 4 inclusive days.
 */
export const isActivitiesRangeBlocked = (
  fromDate: Date | null,
  toDate: Date | null,
  selectedUsers: number[]
): boolean => {
  if (!fromDate || !toDate) return false;
  if ((selectedUsers?.length ?? 0) > 0) return false;
  return differenceInCalendarDays(toDate, fromDate) > ACTIVITIES_MAX_DAYS;
};

const Activities: React.FC = () => {
  const [searchResult, setSearchResult] = useState('');
  //* Epic 80054 - Activities used to query the whole navigation-trace log. It is now bounded by
  //* a date range, defaulted to today like the Audit Log, and applied on Go.
  const [fromDate, setFromDate] = useState<Date | null>(getCurrentESTDate());
  const [toDate, setToDate] = useState<Date | null>(getCurrentESTDate());
  const [error, setError] = useState<string | null>(null);
  const { loading, executeApi } = useApi();
  const settingValue = useSettingValue(SYSTEMSETTINGS.AUDITTRACE_PAGINATION);
  const [isPaginationInitialized, setPaginationInitialized] = useState(false);
  const [pageState, setPageState] = useState<{ data: AuditNavigationTrace[] | null; total: number }>({
    data: null,
    total: 0,
  });
  const [isClientSearchEvaluated, setIsClientSearchEvaluated] = useState(false);
  const [allData, setAllData] = useState<AuditNavigationTrace[]>([]);
  const [handleGoTrigger, setHandleGoTrigger] = useState(-1);
  const isFetching = useRef(false);
  const isFirstLoaded = useRef(false);
  const mounted = React.useRef(true);
  const apiRef = useGridApiRef();
  const { userId } = useUser();
  const { menu } = useMenu();


  //* Epic 80054 - `some` rather than `find` so this is a stable boolean instead of a fresh
  //* object each render, which lets effectiveUserIds below memoise on it.
  const enableAllUsers = Boolean(menu?.some(section =>
    section.listItems?.some(item => item.moduleName === ModuleNames.UsersActivities)
  ));

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10),
  });

  const prevPagination = useRef(paginationModel);
  const prevSearchResult = useRef(searchResult);
  //* Epic 80054 - state, not a ref: the guard and the message that reports it both depend on
  //* whether a user is selected, so a change has to re-render and re-run the effects.
  const [selectedUsers, setSelectedUsers] = useState<number[]>([userId]);

  //* What actually goes on the wire, and therefore what the guard has to judge: a user who
  //* cannot pick users is always scoped to themselves and so is never blocked.
  const effectiveUserIds = useMemo(
    () => (enableAllUsers ? (selectedUsers ?? []) : [userId]),
    [enableAllUsers, selectedUsers, userId]
  );

  const handleUserChange = (selectedUserIds: number[]) => {
    setSelectedUsers(selectedUserIds ?? []);
    setHandleGoTrigger((prev) => prev + 1);
  };

  //* Epic 80054 - the same validations, in the same order, as the Audit Log's Go handler.
  const handleGoClick = () => {
    if (!fromDate || !toDate) {
      setError(GLOBALMESSAGES.BOTH_DATES);
      return;
    }

    if (toDate < fromDate) {
      setError(GLOBALMESSAGES.TO_DATE_GREATER);
      return;
    }

    //* Epic 80054 - a range wider than ACTIVITIES_MAX_DAYS with no user selected scans a huge
    //* volume of trace rows and hangs or times out the grid. Return before setHandleGoTrigger so
    //* loadData never fires the request (no spinner, grid left as-is). Selecting a user scopes
    //* the query, which lifts the restriction.
    if (isActivitiesRangeBlocked(fromDate, toDate, effectiveUserIds)) {
      setError(GLOBALMESSAGES.AUDITLOG_SQLA_RANGE_TOO_WIDE);
      return;
    }

    setError(null);
    setHandleGoTrigger((prev) => prev + 1);
  };

  React.useEffect(() => {
    apiRef?.current?.subscribeEvent('scrollPositionChange', handleScrollPositionChange);
    return () => {
      mounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnRowsScrollEnd: DataGridProProps['onRowsScrollEnd'] = () => {
    if (loading || !pageState?.data) return; // Avoid multiple triggers
    if (isFetching.current) return;

    if (pageState?.data && pageState?.data.length >= pageState?.total) {
      return;
    }

    isFetching.current = true;
    setPaginationModel((prev) => {
      const totalPages = Math.ceil(pageState?.total / prev.pageSize);
      if (prev.page + 1 < totalPages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });

    // Restore previous scroll position after data updates
    setTimeout(() => {
      isFetching.current = false;
    }, 100);
  };

  const loadData = useCallback(async () => {
    //* Epic 80054 - an incomplete range is never sent: the request would either be rejected or
    //* silently fall back to an unbounded scan. handleGoClick surfaces the message.
    if (!fromDate || !toDate) return;
    //* Epic 80054 - handleGoClick blocks this combination before it ever triggers a fetch, but
    //* loadData also runs on search-text and pagination changes, which would still fire it.
    //* Guarding here too means no code path can issue the unbounded query. Returning without
    //* touching pageState leaves the grid as-is and never sets `loading`, so no overlay appears.
    if (isActivitiesRangeBlocked(fromDate, toDate, effectiveUserIds)) return;
    const isNewSearch = paginationModel.page === 0;
    if (isClientSearchEvaluated && isFirstLoaded.current) {
      if (searchResult) {
        const lowerText = searchResult.toLowerCase();
        const filtered = filterDataBySearch(allData, lowerText);
        setPageState({
          data: filtered ?? [],
          total: filtered?.length,
        });
      } else {
        // If no search term, reset to full data
        setPageState({
          data: allData ?? [],
          total: allData?.length,
        });

      }
    }
    else {

      //* Epic 80054 - fromDate/toDate are sent but the API does NOT read them yet:
      //* AuditTraceRequestDto has no such properties and sProc_Portal_AuditNavigationTraces_S
      //* takes no date parameters, so the extra JSON members are ignored and the grid still
      //* returns every row. The guard below is therefore the only thing bounding the query
      //* today. Adding @FromDate/@ToDate to that DTO, AuditLogData and the sproc is what makes
      //* the filter real - no change is needed here when it lands.
      const auditTraceRequest: AuditTraceRequest = {
        fromDate: format(fromDate, GLOBALSETTINGS.DATE_FORMAT_ISO),
        toDate: format(toDate, GLOBALSETTINGS.DATE_FORMAT_ISO),
        searchTerm: searchResult,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        userIds: effectiveUserIds
      }
      const auditTraceData = await executeApi<AuditTraceRequest, AuditNavigationTrace[]>(getNavigationTraces, auditTraceRequest);
      if (auditTraceData && auditTraceData.length > 0) {
        if (!isFirstLoaded.current && auditTraceData[0]?.totalRecords <= paginationModel.pageSize) {
          setIsClientSearchEvaluated(true);
          setAllData(auditTraceData);
        }
        isFirstLoaded.current = true;

        setPageState((prev) => {
          const existingIds = new Set(prev.data?.map((row) => row.navigationId)); // Track existing IDs
          const newData = auditTraceData.filter((row) => !existingIds.has(row.navigationId)); // Filter duplicates

          return {
            data: isNewSearch ? auditTraceData : [...(prev.data || []), ...newData],
            total: auditTraceData.length > 0 && isNewSearch ? auditTraceData[0].totalRecords : prev.total,
          };
        });
      }
      else {
        setPageState({
          data: [],
          total: 0,
        });
      }
    }
    setTimeout(() => {
      apiRef?.current?.scrollToIndexes({ rowIndex: apiRef?.current?.getScrollPosition()?.top });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fromDate, toDate, effectiveUserIds, paginationModel, searchResult, executeApi]);

  useEffect(() => {
    if (settingValue) {
      const newPageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);

      setPaginationModel((prev) => {
        if (prev.pageSize !== newPageSize) {
          return { page: 0, pageSize: newPageSize };
        }
        return prev;
      });
      setPaginationInitialized(true);
    }
  }, [settingValue]);

  //* Epic 80054 - drop the validation message as soon as it stops applying, so the user is not
  //* left reading an error about a range they have already corrected. One effect for all three
  //* messages rather than clearing inline in each picker: only the check that produced the
  //* current message gets to keep it on screen.
  useEffect(() => {
    if (!error) return;

    let stillApplies: boolean;
    if (!fromDate || !toDate) {
      stillApplies = error === GLOBALMESSAGES.BOTH_DATES;
    } else if (toDate < fromDate) {
      stillApplies = error === GLOBALMESSAGES.TO_DATE_GREATER;
    } else {
      stillApplies =
        error === GLOBALMESSAGES.AUDITLOG_SQLA_RANGE_TOO_WIDE &&
        isActivitiesRangeBlocked(fromDate, toDate, effectiveUserIds);
    }

    if (!stillApplies) {
      setError(null);
    }
  }, [error, fromDate, toDate, effectiveUserIds]);

  //* Epic 80054 - dropped the `enableAllUsers` condition: the trigger now also fires from Go,
  //* and the date range is available to every user, not just the ones who can pick a user.
  useEffect(() => {
    if (isPaginationInitialized && handleGoTrigger > -1) {
      setIsClientSearchEvaluated(false);
      isFirstLoaded.current = false;
      if (prevPagination.current.page === 0) {
        loadData();
      }
      else {
        setPaginationModel((prev) => ({
          ...prev,
          page: 0,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGoTrigger])

  useEffect(() => {
    const hasChanged = JSON.stringify(prevPagination.current) !== JSON.stringify(paginationModel);
    const searchChanged = JSON.stringify(prevSearchResult.current) !== JSON.stringify(searchResult);
    if (isPaginationInitialized || hasChanged || searchChanged) {
      prevPagination.current = paginationModel;
      prevSearchResult.current = searchResult;
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationInitialized, paginationModel, searchResult])



  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  }, []);

  const columns: GridColDef[] = [
    { field: 'userName', headerName: 'User', flex: 1, minWidth: 100 },
    {
      field: 'navigationDateTime', headerName: 'Date Time', flex: 1, minWidth: 180,
      valueFormatter: (value) => value ? format(value, GLOBALSETTINGS.DATE_FORMAT_TIME) : ""
    },
    { field: 'objectType', headerName: 'Object Type', flex: 1, minWidth: 100 },
    { field: 'objectName', headerName: 'Object Name', flex: 3, minWidth: 250 },
    { field: 'action', headerName: 'Action', flex: 1, minWidth: 150, hideable: false, filterable: false,sortable: false, disableColumnMenu: false}
  ];

    const {
      columnState,
      handleColumnResize,
    } = useResizableColumns(columns);

  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    GRIDCOLUMNKEYS.ACTIVITIES
  );

    
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <section className='mb-3'>
      <Card>
        <CardContent>
          <Typography variant="h5" component="div" className="mb-3 card-title">Activities</Typography>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Box width="100%" textAlign="left">
              <Typography variant="h5">Select Date Range</Typography>
            </Box>
            <Grid2 container spacing={2} width="100%" alignItems="center">
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Date From"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true, inputProps: { readOnly: true } } }}
                  maxDate={new Date()}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Date To"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true, inputProps: { readOnly: true } } }}
                  maxDate={new Date()}
                />
              </Grid2>
              {enableAllUsers && (<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <UsersDropdownList userId={userId} controlHeight={40} onUserChange={handleUserChange} debounceChange={true} />
                </Box>

              </Grid2>)}

              <Grid2 size={{ xs: 12, sm: 6, md: enableAllUsers ? 3 : 6 }} display="flex">
                <Button id="btnGo" variant="contained" color="primary" onClick={handleGoClick}
                  className='filter-btn'
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                  }}>
                  Go
                </Button>
              </Grid2>
            </Grid2>

            {error &&
              <Alert
                severity="error"
                sx={{
                  mb: "5px",
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  alignSelf: 'flex-start'
                }}
              >
                {error}
              </Alert>
            }

            <Box width="100%" className="search-box-wrap">
              <SearchBox searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER} onSearch={handleSearchText} />
            </Box>

            <Box width="100%">
              <div style={{ height: 500, width: '100%' }}>
                <DataGridPro
                  apiRef={apiRef}
                  rowHeight={dataGridRowHeight}
                  rows={pageState?.data ?? []}
                  {...pageState?.data}
                  columns={columnState}
                  rowCount={pageState?.total}
                  columnHeaderHeight={dataGridHeaderHeight}
                  slots={{
                    loadingOverlay: CustomLoadingOverlay,
                  }}
                  loading={loading}
                  paginationMode="server"
                  getRowId={(row) => row.navigationId}
                  hideFooterPagination
                  // Bug 79916: selecting a row here enables no action, so the footer's
                  // "N rows selected" message only implied a capability that doesn't exist.
                  hideFooterSelectedRowCount
                  onRowsScrollEnd={handleOnRowsScrollEnd}
                  localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
                  onColumnWidthChange={(params) => handleColumnResize(params.width, params.colDef)}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={setColumnVisibilityModel}
                />
              </div>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </section>
    </LocalizationProvider>
  );
};

export default Activities;
